import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@atrvd/saas-ui'
import { useOrganization } from '@/contexts/OrganizationContext'
import { getSupabaseClient } from '@/lib/supabase'
import { invalidateAfterCreditCardManualEdit } from '@/lib/credit-card-invalidation'
import type { CreditCardPatch } from '@/components/banking/credit-cards/creditCardManualValues'

interface SaveCreditCardManualValuesInput {
  bankAccountId: string
  /** Only the fields to change, signed as stored; see {@link CreditCardPatch}. */
  changes: CreditCardPatch
}

/**
 * Save manual values of one credit card (`save_credit_card_manual_values`).
 *
 * A patch: the server changes only the keys `changes` names, so a save never
 * overwrites a figure the user did not touch. The RPC checks `manage_banking`
 * and answers 42501 without it. Stays pending until Credit Card Availability
 * has refetched; a failure is toasted here with the raw message, so callers
 * only react to success.
 */
export function useSaveCreditCardManualValues() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  return useMutation({
    mutationFn: async ({ bankAccountId, changes }: SaveCreditCardManualValuesInput) => {
      if (!orgId) throw new Error('No organization selected')
      const { error } = await getSupabaseClient().rpc('save_credit_card_manual_values', {
        p_org_id: orgId,
        p_bank_account_id: bankAccountId,
        p_changes: changes,
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => invalidateAfterCreditCardManualEdit(queryClient),
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to save the credit card')
    },
  })
}
